import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { languages } from '../i18n/translations';
import { ChevronDown, Check } from 'lucide-react';
import { BR, PT, US, ES, FR, IT, IN, DE, NL, RU, SA, CN, TW } from 'country-flag-icons/react/3x2';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

const LanguageSelector = () => {
  const { language, changeLanguage } = useLanguage();
  const currentLang = languages.find(lang => lang.code === language);

  const flagBoxClass =
    'lang-flag inline-flex shrink-0 h-4 w-6 items-center justify-center overflow-hidden rounded-sm border border-white/20 shadow-sm ' +
    '[&_svg]:!block [&_svg]:!h-full [&_svg]:!w-full [&_svg]:!size-auto [&_svg]:!max-h-none [&_svg]:!max-w-none';

  const FlagIcon = ({ code }) => {
    const flagMap = {
      'pt-BR': BR,
      'pt-PT': PT,
      en: US,
      es: ES,
      fr: FR,
      de: DE,
      it: IT,
      hi: IN,
      nl: NL,
      ru: RU,
      ar: SA,
      'zh-CN': CN,
      'zh-TW': TW
    };
    const FlagComponent = flagMap[code];
    if (!FlagComponent) {
      return (
        <span className={flagBoxClass} aria-hidden>
          🏳️
        </span>
      );
    }
    return (
      <span className={flagBoxClass} aria-hidden>
        <FlagComponent className="block h-full w-full" focusable="false" />
      </span>
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          className="flex items-center gap-2 sm:gap-3 glass-premium hover:bg-white/10 border border-white/10 hover:border-yellow-500/50 text-white backdrop-blur-md transition-all duration-300 px-2.5 sm:px-4 py-2 min-w-0"
        >
          <FlagIcon code={currentLang?.code} />
          <span className="font-medium text-xs sm:text-sm uppercase tracking-wide">{currentLang?.code}</span>
          <ChevronDown className="w-4 h-4 opacity-70 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-[min(20rem,calc(100vw-1.25rem))] max-w-[calc(100vw-1.25rem)] glass-premium backdrop-blur-xl border border-white/10 shadow-2xl overflow-hidden rounded-xl"
        style={{ background: 'rgba(10, 22, 40, 0.95)' }}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-yellow-500/20">
          <div className="flex items-center gap-3 text-xs font-semibold tracking-widest">
            <span className="text-white/60">IDIOMA</span>
            <span className="text-white/20">/</span>
            <span className="text-white/60">LANGUAGE</span>
          </div>
        </div>

        {/* Language Options */}
        <div className="py-2 max-h-[min(24rem,65dvh)] overflow-y-auto overscroll-y-contain">
          {languages.map((lang) => {
            const isActive = language === lang.code;
            return (
              <DropdownMenuItem
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={`flex min-w-0 items-center gap-4 px-5 py-4 cursor-pointer transition-all duration-200 ${
                  isActive 
                    ? 'bg-yellow-500/10 border-l-4 border-yellow-500' 
                    : 'hover:bg-white/5 border-l-4 border-transparent'
                }`}
              >
                {/* Flag - Larger and More Visible */}
                <FlagIcon code={lang.code} />
                
                {/* Code */}
                <span className={`text-sm font-bold uppercase tracking-wider min-w-[40px] ${
                  isActive ? 'text-yellow-500' : 'text-white/70'
                }`}>
                  {lang.code}
                </span>
                
                {/* Separator */}
                <span className="text-white/20">-</span>
                
                {/* Language Name */}
                <span className={`flex-1 min-w-0 text-sm break-words ${
                  isActive ? 'text-white font-semibold' : 'text-white/70'
                }`}>
                  {lang.name}
                </span>
                
                {/* Check Icon for Active */}
                {isActive && (
                  <Check className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                )}
              </DropdownMenuItem>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelector;
